// src/utils/CaDefaultDeductibleUtils.ts
import { PublicConfig } from "@/shared/PublicConfig";
import { Quote } from "@/shared/types/Quote.interface";
import { PlanFilterUtils } from "@/shared/utils/PlanFilterUtils";
import { StateUtils } from "@/shared/utils/StateUtils";

/**
 * ENG-20299 / ENG-20300 — "$750 deductible in California" Statsig test.
 *
 * California quotes and sales fell far harder than Washington's after the plan-design change, so the
 * test lowers the default price there by defaulting the annual deductible to $750 instead of $500.
 * Experiment on `forms-main`, boolean param `caDefaultDeductible`, variant tuid `ca-default-deductible`,
 * targeting gate `forms-main-ca-quoters` on the `ratingState` Statsig attribute (see
 * `useStatsigUserUpdate`).
 *
 * Audience: a quote being CREATED by a quote form (main form, widget) whose rating state is CA and whose
 * policies still sit on the standard default deductible. Existing quotes keep the deductible they were
 * saved with, API-created quotes arrive with the plan picked on the partner's site, and a planId link is
 * a plan the quoter already chose — none of those are "new quoters still on the default".
 *
 * The state is checked in code and not only by the gate: tuid overrides bypass targeting gates, and
 * declaring the winner removes the gate, which must not hand $750 to every state (the T-Mobile lesson,
 * ENG-17768).
 *
 * The treatment is applied ONCE, to the policies of the quote being created, and nothing else follows the
 * flag afterwards: the coverage step's default plan stays on the standard default, so a quote that was
 * never in the test (returning, API-created, or submitted before its state reached Statsig) can never
 * pick up an uncounted $750 later. A pet added on the coverage step copies the first pet's coverage while
 * the pets are linked, which is the default; a pet unlinked by the quoter takes the standard default.
 */
export const CA_DEFAULT_DEDUCTIBLE_TEST = {
    /** Layer parameter on `forms-main`. */
    param: "caDefaultDeductible",
    /** Rating state the test is scoped to. */
    state: "CA",
    /** The default the variant installs. */
    deductible: 750,
    /**
     * Only policies still on the standard default move. A priority code whose default plan carries its own
     * deductible is that partner's deal, not something the test should rewrite.
     */
    baselineDeductible: PublicConfig.PTZ_US.DEFAULT_ANNUAL_DEDUCTIBLE
} as const;

export type NewQuoteAudienceParams = {
    policies: Quote["policies"];
    ratingState?: string;
    /** A planId on the URL (planCoverageOverride): the quoter arrived with a plan already chosen. */
    hasPlanOverride?: boolean;
};

export class CaDefaultDeductibleUtils {
    static isTargetState(ratingState?: string): boolean {
        return StateUtils.normalizeStateCode(ratingState) === CA_DEFAULT_DEDUCTIBLE_TEST.state;
    }

    static isOnBaselineDeductible(policy: NonNullable<Quote["policies"]>[number] | undefined): boolean {
        return policy?.coverageSettings?.amounts?.annualDeductible === CA_DEFAULT_DEDUCTIBLE_TEST.baselineDeductible;
    }

    /**
     * Whether a quote about to be created is in the experiment's audience. The SAME predicate gates the
     * treatment and the exposure, for both arms, so control and variant count exactly the same quoters.
     */
    static isEligibleNewQuote({ policies, ratingState, hasPlanOverride }: NewQuoteAudienceParams): boolean {
        if (hasPlanOverride) return false;
        if (!CaDefaultDeductibleUtils.isTargetState(ratingState)) return false;
        return !!policies?.some(policy => CaDefaultDeductibleUtils.isOnBaselineDeductible(policy));
    }

    /** The variant treatment: every policy still on the baseline deductible moves to the test deductible. */
    static applyToPolicies(policies: Quote["policies"]): Quote["policies"] {
        if (!policies?.length) return policies;
        return policies.map(policy =>
            CaDefaultDeductibleUtils.isOnBaselineDeductible(policy)
                ? { ...policy, coverageSettings: PlanFilterUtils.withDeductible(policy.coverageSettings, CA_DEFAULT_DEDUCTIBLE_TEST.deductible) }
                : policy
        );
    }
}
